import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Eye, Compass, Sun, Crown, LogOut, Settings, Star, 
  ChevronRight, Check, TrendingUp, BookOpen, User, ChevronDown, ChevronUp, Shield, Heart,
  Flame, ChevronLeft, Calendar, RefreshCw, CheckCircle2, Target, Scale, Users,
  Phone, FileText, Download, ScrollText
} from "lucide-react";
import { useAuth, apiCall } from "../App";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { JourneyProgressBar, ClientInterventionCard } from "../components/JourneyPanel";
import LifeDiagnostic from "../components/LifeDiagnostic";
import KoshaQuiz from "../components/KoshaQuiz";
import RelationshipDiagnostic from "../components/RelationshipDiagnostic";
import AdminViewBreadcrumb from "../components/AdminViewBreadcrumb";
import TatkalikQuiz from "../components/TatkalikQuiz";
import IssueDiagnosis from "../components/IssueDiagnosis";
import DemoVideoButton from "../components/DemoVideo";
import NotificationBell from "../components/NotificationBell";
import SessionDropdown from "../components/SessionDropdown";
import PushToggle from "../components/PushToggle";
import SubscriptionBar from "../components/SubscriptionBar";
import OrderReportModal from "../components/OrderReportModal";
import OrderCoachAssessmentModal from "../components/OrderCoachAssessmentModal";
import BookCallModal from "../components/BookCallModal";
import DynamicCtaModal from "../components/DynamicCtaModal";
import UserGuide from "../components/UserGuide";
import ReferralCard from "../components/ReferralCard";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, 
  Radar, ResponsiveContainer
} from "recharts";

import { PILLAR_COLORS, PillarSection, PracticeCard, AlignmentSection, PipelineContent, VerseCard, HarmonyItem, DiagnosticResultsBucket } from "../components/DashboardWidgets";
const RelationshipModule = lazy(() => import("../components/RelationshipModule"));
const LifeModule = lazy(() => import("../components/LifeModule"));

const ClientDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewAsUserId = searchParams.get('view_as');
  const fromTab = searchParams.get('from_tab');
  const isCoachView = !!viewAsUserId;
  const [viewAsName, setViewAsName] = useState('');
  const [awareness, setAwareness] = useState(null);
  const [alignment, setAlignment] = useState(null);
  const [awakening, setAwakening] = useState(null);
  const [harmony, setHarmony] = useState(null);
  const [progress, setProgress] = useState(null);
  const [overallCompliance, setOverallCompliance] = useState(null);
  const [koshaState, setKoshaState] = useState(null);
  const [activeModule, setActiveModule] = useState(() => {
    const access = user?.module_access || [];
    if (access.includes("relationship")) return "relationship";
    if (access.includes("life")) return "life";
    return "relationship";
  });
  const [showGuide, setShowGuide] = useState(false);
  const [moduleTaglines, setModuleTaglines] = useState({
    relationship: "Awareness → Alignment → Awakening through the 175-trigger Vedic lens.",
    life: "9-module Kosha-deep diagnostic into your purpose, pranic flow, and dharma.",
  });
  const [practiceAssignments, setPracticeAssignments] = useState([]);
  const [streakData, setStreakData] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => { const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() + 1 }; });
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pipelineCompleted, setPipelineCompleted] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [lifeCompliance, setLifeCompliance] = useState(null);
  const [lifeWeeklySummary, setLifeWeeklySummary] = useState(null);
  const [relKoshaRetakeKey, setRelKoshaRetakeKey] = useState(0);
  const [lifeKoshaRetakeKey, setLifeKoshaRetakeKey] = useState(0);
  const [issueDiagnosis, setIssueDiagnosis] = useState(null);
  const [myInvoices, setMyInvoices] = useState([]);
  const [lifeIssueDiagnosis, setLifeIssueDiagnosis] = useState(null);
  const [relPipeline, setRelPipeline] = useState(null);
  const [lifePipeline, setLifePipeline] = useState(null);
  const [relTatkalikDone, setRelTatkalikDone] = useState(false);
  const [lifeTatkalikDone, setLifeTatkalikDone] = useState(false);
  const [relKoshaDone, setRelKoshaDone] = useState(false);
  const [lifeKoshaDone, setLifeKoshaDone] = useState(false);
  const [ctaSettings, setCtaSettings] = useState(null);
  const [clientReports, setClientReports] = useState([]);
  const [showOrderReport, setShowOrderReport] = useState(false);
  const [showOrderCoachAssessment, setShowOrderCoachAssessment] = useState(false);
  const [coachAssessmentActive, setCoachAssessmentActive] = useState(false);
  const [showBookCall, setShowBookCall] = useState(false);
  const [selectedDynamicCta, setSelectedDynamicCta] = useState(null);

  const handleKoshaStateChange = (quizType) => {
    // Increment key to force results instance to re-mount and re-fetch
    if (quizType === "relationship") setRelKoshaRetakeKey(prev => prev + 1);
    else setLifeKoshaRetakeKey(prev => prev + 1);
  };

  const handleRelRetakeAll = async () => {
    // Immediately clear all frontend state to avoid stale display
    setAwareness(null);
    setAlignment(null);
    setAwakening(null);
    setHarmony(null);
    setOverallCompliance(null);
    setLifeCompliance(null);
    setProgress(null);
    // Force JourneyProgressBar + Life components to re-mount
    setLifeDiagnosticKey(prev => prev + 1);
    setRelKoshaRetakeKey(prev => prev + 1);

    // Re-fetch ALL dashboard data since profiling scores and downstream are cleared
    const [awarenessRes, alignmentRes, awakeningRes, harmonyRes, complianceRes, progressRes, lifeCompRes] = await Promise.all([
      apiCall("GET", "/dashboard/awareness", null, token),
      apiCall("GET", "/dashboard/alignment", null, token),
      apiCall("GET", "/dashboard/awakening", null, token),
      apiCall("GET", "/dashboard/harmony?module=relationship", null, token),
      apiCall("GET", "/dashboard/overall_compliance", null, token),
      apiCall("GET", "/dashboard/progress", null, token),
      apiCall("GET", "/dashboard/overall_compliance?module=life", null, token),
    ]);
    if (awarenessRes.success) setAwareness(awarenessRes.data);
    if (alignmentRes.success) setAlignment(alignmentRes.data);
    if (awakeningRes.success) setAwakening(awakeningRes.data);
    if (harmonyRes.success) setHarmony(harmonyRes.data);
    if (complianceRes.success) setOverallCompliance(complianceRes.data);
    if (progressRes.success) setProgress(progressRes.data);
    if (lifeCompRes.success) setLifeCompliance(lifeCompRes.data);
  };

  const handleLifeRetakeAll = async () => {
    // Immediately clear all Life state to avoid stale display
    setLifeCompliance(null);
    setProgress(null);
    // Force all Life components to re-mount
    setLifeDiagnosticKey(prev => prev + 1);
    setLifeKoshaRetakeKey(prev => prev + 1);
    // Re-fetch Life compliance + progress + harmony
    const [progressRes, lifeCompRes, harmonyRes] = await Promise.all([
      apiCall("GET", "/dashboard/progress", null, token),
      apiCall("GET", "/dashboard/overall_compliance?module=life", null, token),
      apiCall("GET", `/dashboard/harmony?module=${activeModule}`, null, token),
    ]);
    if (progressRes.success) setProgress(progressRes.data);
    if (lifeCompRes.success) setLifeCompliance(lifeCompRes.data);
    if (harmonyRes.success) setHarmony(harmonyRes.data);
  };

  // Force key to re-mount child components on data changes
  const [lifeDiagnosticKey, setLifeDiagnosticKey] = useState(0);

  const refreshCompliance = async () => {
    const res = await apiCall("GET", "/dashboard/overall_compliance?module=relationship", null, token);
    if (res.success) setOverallCompliance(res.data);
    const lifeRes = await apiCall("GET", "/dashboard/overall_compliance?module=life", null, token);
    if (lifeRes.success) setLifeCompliance(lifeRes.data);
  };

  const fetchPipelineProgress = async () => {
    const res = await apiCall("GET", "/dashboard/pipeline-progress", null, token);
    if (res.success) setPipelineCompleted(res.data.completed_items || []);
  };

  const handleTogglePipeline = async (itemKey) => {
    const res = await apiCall("POST", `/dashboard/pipeline-progress/toggle?item_key=${encodeURIComponent(itemKey)}`, null, token);
    if (res.success) {
      setPipelineCompleted(res.data.completed_items || []);
      toast.success(res.data.action === "checked" ? "Progress recorded!" : "Item unchecked");
      refreshCompliance();
      apiCall("GET", "/dashboard/weekly-summary?module=relationship", null, token).then(r => { if (r.success) setWeeklySummary(r.data); });
      apiCall("GET", "/dashboard/weekly-summary?module=life", null, token).then(r => { if (r.success) setLifeWeeklySummary(r.data); });
    }
  };

  const [regenerating, setRegenerating] = useState(false);
  const handleRegeneratePipeline = async () => {
    setRegenerating(true);
    const res = await apiCall("POST", "/dashboard/regenerate-pipeline", null, token);
    if (res.success && res.data.regenerated) {
      toast.success("Pipeline regenerated with fresh personalized content");
      const [relP, lifeP] = await Promise.all([
        apiCall("GET", "/pipeline/relationship", null, token),
        apiCall("GET", "/pipeline/life", null, token),
      ]);
      if (relP.success) setRelPipeline(relP.data);
      if (lifeP.success) setLifePipeline(lifeP.data);
    } else {
      toast.error(res.data?.message || "Could not regenerate pipeline");
    }
    setRegenerating(false);
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (isCoachView) {
        // Fetch the SAME individual endpoints as the client, but with user_id param
        const uid = viewAsUserId;
        const [awarenessRes, alignmentRes, awakeningRes, harmonyRes, progressRes, complianceRes, koshaRes, practiceRes, nameRes, issueDiagRes, lifeIssueDiagRes, relTatkalikRes, lifeTatkalikRes, relKoshaQuizRes, lifeKoshaQuizRes] = await Promise.all([
          apiCall("GET", `/dashboard/awareness?user_id=${uid}`, null, token),
          apiCall("GET", `/dashboard/alignment?user_id=${uid}`, null, token),
          apiCall("GET", `/dashboard/awakening?user_id=${uid}`, null, token),
          apiCall("GET", `/dashboard/harmony?user_id=${uid}&module=relationship`, null, token),
          apiCall("GET", `/dashboard/progress?user_id=${uid}`, null, token),
          apiCall("GET", `/dashboard/overall_compliance?user_id=${uid}`, null, token),
          apiCall("GET", `/kosha/client/${uid}`, null, token),
          apiCall("GET", `/practice-assignments/client/${uid}`, null, token),
          apiCall("GET", `/coach/client/${uid}/full_dashboard`, null, token),
          apiCall("GET", `/issue-diagnosis/results?user_id=${uid}&module=relationship`, null, token),
          apiCall("GET", `/issue-diagnosis/results?user_id=${uid}&module=life`, null, token),
          apiCall("GET", `/tatkalik/results?module=relationship&user_id=${uid}`, null, token),
          apiCall("GET", `/tatkalik/results?module=life&user_id=${uid}`, null, token),
          apiCall("GET", `/kosha-quiz/results/relationship/${uid}`, null, token),
          apiCall("GET", `/kosha-quiz/results/life/${uid}`, null, token),
        ]);
        if (awarenessRes.success) setAwareness(awarenessRes.data);
        if (alignmentRes.success) setAlignment(alignmentRes.data);
        if (awakeningRes.success) setAwakening(awakeningRes.data);
        if (harmonyRes.success) setHarmony(harmonyRes.data);
        if (progressRes.success) setProgress(progressRes.data);
        if (complianceRes.success) setOverallCompliance(complianceRes.data);
        if (koshaRes.success) setKoshaState(koshaRes.data);
        if (practiceRes.success) setPracticeAssignments(practiceRes.data.assignments || []);
        if (nameRes.success) setViewAsName(nameRes.data.client_name);
        if (issueDiagRes.success) setIssueDiagnosis(issueDiagRes.data);
        if (lifeIssueDiagRes.success) setLifeIssueDiagnosis(lifeIssueDiagRes.data);
        if (relTatkalikRes.success) setRelTatkalikDone(!!relTatkalikRes.data?.has_response);
        if (lifeTatkalikRes.success) setLifeTatkalikDone(!!lifeTatkalikRes.data?.has_response);
        if (relKoshaQuizRes.success) setRelKoshaDone(!!relKoshaQuizRes.data?.completed);
        if (lifeKoshaQuizRes.success) setLifeKoshaDone(!!lifeKoshaQuizRes.data?.completed);
        // Fetch pipeline data (non-blocking)
        Promise.all([
          apiCall("GET", "/pipeline/relationship", null, token),
          apiCall("GET", "/pipeline/life", null, token),
          apiCall("GET", `/dashboard/pipeline-progress?user_id=${uid}`, null, token),
          apiCall("GET", `/dashboard/weekly-summary?user_id=${uid}&module=relationship`, null, token),
          apiCall("GET", `/dashboard/overall_compliance?user_id=${uid}&module=life`, null, token),
          apiCall("GET", `/dashboard/weekly-summary?user_id=${uid}&module=life`, null, token),
        ]).then(([relP, lifeP, pipeP, weekP, lifeCompP, lifeWeekP]) => {
          if (relP.success) setRelPipeline(relP.data);
          if (lifeP.success) setLifePipeline(lifeP.data);
          if (pipeP.success) setPipelineCompleted(pipeP.data.completed_items || []);
          if (weekP.success) setWeeklySummary(weekP.data);
          if (lifeCompP.success) setLifeCompliance(lifeCompP.data);
          if (lifeWeekP.success) setLifeWeeklySummary(lifeWeekP.data);
        });
        // Fetch CTA settings and client reports (non-blocking, for admin/coach view too)
        apiCall("GET", "/reports-3a/cta-settings", null, token).then(r => { if (r.success) setCtaSettings(r.data); });
        apiCall("GET", `/reports-3a/list?client_id=${uid}`, null, token).then(r => { if (r.success) setClientReports(r.data.reports || []); });
        setLoading(false);
        return;
      }

      const [awarenessRes, alignmentRes, awakeningRes, harmonyRes, progressRes, complianceRes, koshaRes, practiceRes, issueDiagRes, lifeIssueDiagRes, relTatkalikRes, lifeTatkalikRes, relKoshaQuizRes, lifeKoshaQuizRes] = await Promise.all([
        apiCall("GET", "/dashboard/awareness", null, token),
        apiCall("GET", "/dashboard/alignment", null, token),
        apiCall("GET", "/dashboard/awakening", null, token),
        apiCall("GET", `/dashboard/harmony?module=${activeModule}`, null, token),
        apiCall("GET", "/dashboard/progress", null, token),
        apiCall("GET", "/dashboard/overall_compliance", null, token),
        apiCall("GET", `/kosha/client/${user?.user_id}`, null, token),
        apiCall("GET", `/practice-assignments/client/${user?.user_id}`, null, token),
        apiCall("GET", "/issue-diagnosis/results?module=relationship", null, token),
        apiCall("GET", "/issue-diagnosis/results?module=life", null, token),
        apiCall("GET", "/tatkalik/results?module=relationship", null, token),
        apiCall("GET", "/tatkalik/results?module=life", null, token),
        apiCall("GET", `/kosha-quiz/results/relationship/${user?.user_id}`, null, token),
        apiCall("GET", `/kosha-quiz/results/life/${user?.user_id}`, null, token),
      ]);

      if (awarenessRes.success) setAwareness(awarenessRes.data);
      if (alignmentRes.success) setAlignment(alignmentRes.data);
      if (awakeningRes.success) setAwakening(awakeningRes.data);
      if (harmonyRes.success) setHarmony(harmonyRes.data);
      if (progressRes.success) setProgress(progressRes.data);
      if (complianceRes.success) setOverallCompliance(complianceRes.data);
      if (koshaRes.success) setKoshaState(koshaRes.data);
      if (practiceRes.success) setPracticeAssignments(practiceRes.data.assignments || []);
      if (issueDiagRes.success) setIssueDiagnosis(issueDiagRes.data);
      if (lifeIssueDiagRes.success) setLifeIssueDiagnosis(lifeIssueDiagRes.data);
      if (relTatkalikRes.success) setRelTatkalikDone(!!relTatkalikRes.data?.has_response);
      if (lifeTatkalikRes.success) setLifeTatkalikDone(!!lifeTatkalikRes.data?.has_response);
      if (relKoshaQuizRes.success) setRelKoshaDone(!!relKoshaQuizRes.data?.completed);
      if (lifeKoshaQuizRes.success) setLifeKoshaDone(!!lifeKoshaQuizRes.data?.completed);
      // Fetch pipeline data (non-blocking)
      Promise.all([
        apiCall("GET", "/pipeline/relationship", null, token),
        apiCall("GET", "/pipeline/life", null, token),
        apiCall("GET", "/dashboard/pipeline-progress", null, token),
        apiCall("GET", "/dashboard/weekly-summary?module=relationship", null, token),
        apiCall("GET", "/dashboard/overall_compliance?module=life", null, token),
        apiCall("GET", "/dashboard/weekly-summary?module=life", null, token),
      ]).then(([relP, lifeP, pipeP, weekP, lifeCompP, lifeWeekP]) => {
        if (relP.success) setRelPipeline(relP.data);
        if (lifeP.success) setLifePipeline(lifeP.data);
        if (pipeP.success) setPipelineCompleted(pipeP.data.completed_items || []);
        if (weekP.success) setWeeklySummary(weekP.data);
        if (lifeCompP.success) setLifeCompliance(lifeCompP.data);
        if (lifeWeekP.success) setLifeWeeklySummary(lifeWeekP.data);
      });
      // Fetch CTA settings and client reports (non-blocking)
      apiCall("GET", "/reports-3a/cta-settings", null, token).then(r => { if (r.success) setCtaSettings(r.data); });
      apiCall("GET", "/reports-3a/list", null, token).then(r => { if (r.success) setClientReports(r.data.reports || []); });
      if (!isCoachView && user?.role === "client") {
        apiCall("GET", "/invoices/my", null, token).then(r => { if (r.success) setMyInvoices(r.data.invoices || []); });
      }
      setLoading(false);
    };

    fetchDashboardData();
  }, [token, user, navigate, isCoachView, viewAsUserId]);

  const handleCompletePractice = async (practiceId, isCurrentlyCompleted) => {
    const result = await apiCall("POST", `/dashboard/alignment/complete?practice_id=${practiceId}`, null, token);
    if (result.success) {
      toast.success(isCurrentlyCompleted ? "Practice unchecked" : "Sadhana completed! Keep going.");
      const alignmentRes = await apiCall("GET", "/dashboard/alignment", null, token);
      if (alignmentRes.success) setAlignment(alignmentRes.data);
      refreshCompliance();
    }
  };

  const handleToggleVerse = async (verseId, isCurrentlyCompleted) => {
    const result = await apiCall("POST", `/dashboard/awakening/toggle_verse?verse_id=${verseId}`, null, token);
    if (result.success) {
      toast.success(isCurrentlyCompleted ? "Verse unchecked" : "Verse recited!");
      const awakeningRes = await apiCall("GET", "/dashboard/awakening", null, token);
      if (awakeningRes.success) setAwakening(awakeningRes.data);
      refreshCompliance();
    }
  };

  const handleToggleHarmony = async (itemId, isCurrentlyCompleted) => {
    const result = await apiCall("POST", `/dashboard/harmony/toggle?item_id=${itemId}&module=${activeModule}`, null, token);
    if (result.success) {
      toast.success(isCurrentlyCompleted ? "Item unchecked" : "Progress recorded!");
      const harmonyRes = await apiCall("GET", `/dashboard/harmony?module=${activeModule}`, null, token);
      if (harmonyRes.success) setHarmony(harmonyRes.data);
      refreshCompliance();
    }
  };

  const handleTogglePracticeCompliance = async (assignmentId) => {
    const res = await apiCall("PUT", `/practice-assignments/${assignmentId}/compliance`, null, token);
    if (res.success) {
      setPracticeAssignments(prev => prev.map(a =>
        a.assignment_id === assignmentId ? { ...a, compliance_status: res.data.compliance_status } : a
      ));
      toast.success(res.data.compliance_status === "completed" ? "Practice completed!" : "Practice unchecked");
      // Refresh streak & calendar
      fetchCalendar(calendarMonth.year, calendarMonth.month);
    }
  };

  const fetchCalendar = useCallback(async (year, month) => {
    const uid = isCoachView ? viewAsUserId : user?.user_id;
    if (!uid) return;
    const res = await apiCall("GET", `/practice-calendar/${uid}?year=${year}&month=${month}`, null, token);
    if (res.success) {
      setCalendarData(res.data);
      setStreakData({ current_streak: res.data.current_streak, longest_streak: res.data.longest_streak, total_days: res.data.total_days });
    }
  }, [token, isCoachView, viewAsUserId, user?.user_id]);

  useEffect(() => {
    if (activeModule === "life") fetchCalendar(calendarMonth.year, calendarMonth.month);
  }, [activeModule, calendarMonth, fetchCalendar]);

  // Re-fetch harmony data when module switches (module-specific tracking)
  useEffect(() => {
    if (!token) return;
    const fetchModuleHarmony = async () => {
      const harmonyRes = await apiCall("GET", `/dashboard/harmony?module=${activeModule}`, null, token);
      if (harmonyRes.success) setHarmony(harmonyRes.data);
    };
    fetchModuleHarmony();
  }, [activeModule, token]);

  // Fetch admin-editable module taglines once on mount
  useEffect(() => {
    apiCall("GET", "/module-taglines", null, token).then(res => {
      if (res.success && res.data.taglines) setModuleTaglines(res.data.taglines);
    });
  }, [token]);

  // Fetch coach-assessment activation status
  const refreshCoachAssessmentStatus = useCallback(async () => {
    const res = await apiCall("GET", "/coach-assessment-orders/status", null, token);
    if (res.success) setCoachAssessmentActive(!!res.data.active);
  }, [token]);
  useEffect(() => {
    if (token) refreshCoachAssessmentStatus();
  }, [token, refreshCoachAssessmentStatus]);

  const changeMonth = (delta) => {
    setCalendarMonth(prev => {
      let m = prev.month + delta;
      let y = prev.year;
      if (m > 12) { m = 1; y++; }
      if (m < 1) { m = 12; y--; }
      return { year: y, month: m };
    });
    setSelectedCalendarDate(null);
  };

  const energyChartData = awareness?.energy ? [
    { subject: "Direction", A: awareness.energy.masculine * 100, fullMark: 100 },
    { subject: "Stability", A: awareness.energy.masculine * 90, fullMark: 100 },
    { subject: "Emotional", A: awareness.energy.feminine * 100, fullMark: 100 },
    { subject: "Nurturing", A: awareness.energy.feminine * 90, fullMark: 100 },
    { subject: "Harmony", A: (awareness.energy?.harmony_potential || 0) * 1000, fullMark: 100 },
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-void flex items-center justify-center">
        <div className="text-divine-gold animate-pulse font-heading text-2xl">
          Loading Your Sacred Dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cosmic-void" data-testid="client-dashboard">
      {/* Admin/Coach View Breadcrumb */}
      {isCoachView && (
        <AdminViewBreadcrumb
          viewerRole={user?.role}
          fromTab={fromTab}
          entityType="client"
          entityName={viewAsName || viewAsUserId}
          testId="client-view-breadcrumb"
        />
      )}

      {/* Navigation */}
      <nav className={`fixed ${isCoachView ? 'top-8' : 'top-0'} w-full z-50 backdrop-blur-md border-b border-divine-gold/10 bg-cosmic-void/80`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-3xl text-divine-gold">ॐ</span>
            <span className="font-heading text-xl text-divine-gold tracking-wider hidden md:block">
              SIWON HARMONY
            </span>
          </Link>
          
          <div className="flex items-center gap-4">
            {(!user?.module_access || user?.module_access?.length === 0) && !isCoachView && (
              <Button className="vedic-button-secondary text-sm py-2" onClick={() => navigate("/pricing")} data-testid="upgrade-btn">
                <Crown className="w-4 h-4 mr-2" />
                Subscribe
              </Button>
            )}
            
            <PushToggle token={token} />
            <NotificationBell token={token} />
            <SessionDropdown token={token} />

            <Link to="/profile">
              <Button variant="ghost" className="text-chakra-crown/70 hover:text-divine-gold" data-testid="profile-btn">
                <User className="w-5 h-5" />
              </Button>
            </Link>
            
            <div className="flex items-center gap-2 text-chakra-crown/70">
              <span className="hidden md:inline">{user?.name}</span>
              <button 
                onClick={logout}
                className="p-2 hover:text-divine-gold transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="font-heading text-3xl md:text-4xl text-divine-gold tracking-wide">
                  SIWON Dashboard
                </h1>
                <p className="text-chakra-crown/60 mt-2">
                  {progress?.retake_pending 
                    ? "Retake in progress — complete your diagnostic to resume" 
                    : progress?.current_week 
                      ? `Week ${progress.current_week} • ${progress.phases?.[progress.current_week]?.name || "Awareness"} Phase` 
                      : "Welcome! Choose a module and begin your journey"}
                </p>
              </div>
            </div>
            {/* CTA Buttons Grid */}
            {(() => {
              const access = isCoachView || ["admin","coach","expert"].includes(user?.role) ? ["relationship","life"] : (user?.module_access || []);
              const hasActiveAccess = access.includes(activeModule);
              if (!hasActiveAccess) return null;
              const region = user?.client_region || "domestic";
              const showLegacy = region === "domestic" || !region;
              return (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-2" data-testid="cta-buttons-grid">
                  {showLegacy && ctaSettings?.book_call_enabled && (
                    <button
                      className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-chakra-heart/30 text-chakra-heart hover:bg-chakra-heart/10 transition-all min-h-[72px]"
                      data-testid="book-call-btn"
                      onClick={() => setShowBookCall(true)}
                    >
                      <Phone className="w-5 h-5 shrink-0" />
                      <span className="text-[10px] leading-tight text-center font-medium">{ctaSettings.book_call_label || "Book 1:1 Call"}</span>
                    </button>
                  )}
                  {showLegacy && ctaSettings?.order_report_enabled && (
                    <button
                      className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-purple-500/30 text-purple-400 hover:bg-purple-500/10 transition-all min-h-[72px]"
                      data-testid="order-report-btn"
                      onClick={() => setShowOrderReport(true)}
                    >
                      <FileText className="w-5 h-5 shrink-0" />
                      <span className="text-[10px] leading-tight text-center font-medium">{ctaSettings.order_report_label || "Order Report"}</span>
                    </button>
                  )}
                  {activeModule === "relationship" && !coachAssessmentActive && (
                    <button
                      className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-divine-gold/40 text-divine-gold hover:bg-divine-gold/10 transition-all min-h-[72px]"
                      data-testid="order-coach-assessment-btn"
                      onClick={() => setShowOrderCoachAssessment(true)}
                    >
                      <ScrollText className="w-5 h-5 shrink-0" />
                      <span className="text-[10px] leading-tight text-center font-medium">Order Coach Assessment</span>
                    </button>
                  )}
                  {activeModule === "relationship" && (
                    <button
                      className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border transition-all min-h-[72px] ${
                        coachAssessmentActive
                          ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/15"
                          : "border-amber-500/30 text-amber-300 bg-amber-500/5 hover:bg-amber-500/10"
                      }`}
                      data-testid="open-coach-assessment-btn"
                      onClick={() => navigate("/coach-assessment-matrix")}
                      title={coachAssessmentActive ? "Open your Coach Assessment Matrix" : "Preview the Coach Assessment Matrix (awaiting activation)"}
                    >
                      <ScrollText className="w-5 h-5 shrink-0" />
                      <span className="text-[10px] leading-tight text-center font-medium">
                        Coach Assessment {coachAssessmentActive ? "· Open" : "· Preview"}
                      </span>
                    </button>
                  )}
                  {ctaSettings?.cta_buttons?.filter(c => c.enabled && (c.target_audience === "all" || c.target_audience === region)).map(cta => (
                    <button
                      key={cta.cta_id}
                      className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border transition-all min-h-[72px] ${cta.target_audience === "international" ? "border-sky-400/30 text-sky-400 hover:bg-sky-400/10" : "border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"}`}
                      data-testid={`dynamic-cta-${cta.cta_id}`}
                      onClick={() => setSelectedDynamicCta(cta)}
                    >
                      {cta.cta_type === "coaching_call" ? <Phone className="w-5 h-5 shrink-0" /> : <FileText className="w-5 h-5 shrink-0" />}
                      <span className="text-[10px] leading-tight text-center font-medium">{cta.label}</span>
                    </button>
                  ))}
                  {clientReports.filter(r => r.status === "finalized").length > 0 && (
                    <button
                      className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 transition-all min-h-[72px]"
                      data-testid="download-report-btn"
                      onClick={async () => {
                      const report = clientReports.find(r => r.status === "finalized") || clientReports[0];
                      try {
                        const res = await fetch(
                          `${process.env.REACT_APP_BACKEND_URL}/api/reports-3a/download-pdf/${report.report_id}`,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (!res.ok) throw new Error("Download failed");
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `3A_Report_${report.item_name || "report"}.pdf`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                      } catch {
                        toast.error("Could not download report");
                      }
                    }}
                    >
                      <Download className="w-5 h-5 shrink-0" />
                      <span className="text-[10px] leading-tight text-center font-medium">Download Report</span>
                    </button>
                  )}
                  <button
                    className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10 transition-all min-h-[72px] ${showGuide ? 'bg-divine-gold/10' : ''}`}
                    data-testid="user-guide-btn"
                    onClick={() => setShowGuide(!showGuide)}
                  >
                    <BookOpen className="w-5 h-5 shrink-0" />
                    <span className="text-[10px] leading-tight text-center font-medium">User Guide</span>
                  </button>
                  <DemoVideoButton />
                </div>
              );
            })()}
          </div>

          {/* Module Selector */}
          {showGuide ? (
            <UserGuide activeModule={activeModule} onClose={() => setShowGuide(false)} moduleAccess={isCoachView || ["admin","coach","expert"].includes(user?.role) ? ["relationship","life"] : (user?.module_access || [])} />
          ) : (
          <>
          {/* Subscription Status Bar */}
          {!isCoachView && user?.role === "client" && (
            <div className="mb-4">
              <SubscriptionBar token={token} user={user} activeModule={activeModule} />
              {/* Invoice Downloads */}
              {myInvoices.length > 0 && (
                <div className="mt-2 flex items-center gap-3 px-4 py-2 rounded-xl bg-cosmic-light/5 border border-divine-gold/10" data-testid="my-invoices-bar">
                  <FileText className="w-4 h-4 text-divine-gold/50 shrink-0" />
                  <span className="text-chakra-crown/50 text-xs">Invoices:</span>
                  <div className="flex gap-2 flex-wrap">
                    {myInvoices.map(inv => (
                      <a key={inv.invoice_number}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/download/${inv.order_id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="px-2 py-0.5 rounded-lg bg-divine-gold/10 border border-divine-gold/20 text-divine-gold text-xs hover:bg-divine-gold/20 transition-all"
                        data-testid={`invoice-download-${inv.invoice_number}`}
                        onClick={(e) => {
                          e.preventDefault();
                          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/download/${inv.order_id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          }).then(r => r.blob()).then(b => {
                            const url = URL.createObjectURL(b);
                            const a = document.createElement("a");
                            a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
                            URL.revokeObjectURL(url);
                          });
                        }}>
                        {inv.invoice_number} — ₹{inv.total?.toLocaleString()}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <ReferralCard token={token} />
            </div>
          )}
          {/* ── Reflective practice note (visible across both modules) ── */}
          <div className="mb-6 p-4 rounded-2xl border border-divine-gold/30 bg-gradient-to-br from-divine-gold/8 via-cosmic-light/5 to-chakra-crown/5 flex items-start gap-3"
            data-testid="reflective-note">
            <div className="shrink-0 w-9 h-9 rounded-full bg-divine-gold/15 border border-divine-gold/30 flex items-center justify-center mt-0.5">
              <span className="text-xl text-divine-gold leading-none">॥</span>
            </div>
            <div className="flex-1">
              <p className="text-xs font-heading tracking-[0.18em] uppercase text-divine-gold mb-1.5">
                A WORD BEFORE YOU BEGIN
              </p>
              <p className="text-sm text-chakra-crown/85 leading-relaxed italic">
                These questions are meant to be answered with deep reflection, not speed.
                They&apos;re also here to gently test—and build—your patience.
                So please don&apos;t rush; give yourself time and answer with
                <span className="text-divine-gold/90 not-italic font-medium"> honesty</span> and
                <span className="text-divine-gold/90 not-italic font-medium"> integrity</span>.
              </p>
            </div>
          </div>

          <div className="flex gap-3 mb-8" data-testid="module-selector">
            {[
              { id: "relationship", label: "Relationship Wisdom Coaching", icon: "♡", desc: "3A Journey" },
              { id: "life", label: "Life Wisdom Coaching", icon: "☸", desc: "Kosha & Practices" },
            ].map(m => {
              const hasAccess = isCoachView || user?.role === "admin" || user?.role === "coach" || user?.role === "expert" || (user?.module_access || []).includes(m.id);
              if (!hasAccess) return null;
              const tagline = (moduleTaglines[m.id] || "").trim();
              return (
                <button key={m.id}
                  onClick={() => setActiveModule(m.id)}
                  className={`flex-1 p-4 rounded-xl border transition-all text-left ${
                    activeModule === m.id
                      ? "bg-divine-gold/10 border-divine-gold text-divine-gold"
                      : "bg-cosmic-light/5 border-divine-gold/10 text-chakra-crown/60 hover:border-divine-gold/30"
                  }`}
                  data-testid={`module-${m.id}`}
                >
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl leading-none">{m.icon}</span>
                    <span className="font-heading text-sm">{m.label}</span>
                    <span className="text-xs opacity-50">— {m.desc}</span>
                  </div>
                  {tagline && (
                    <p className="text-xs opacity-70 leading-snug italic" data-testid={`module-tagline-${m.id}`}>
                      {tagline}
                    </p>
                  )}
                </button>
              );
            })}
            {/* Upgrade prompt for single-module users */}
            {!isCoachView && user?.role === "client" && (user?.module_access || []).length === 1 && (
              <button onClick={() => navigate("/pricing")}
                className="flex-1 p-4 rounded-xl border border-dashed border-divine-gold/20 text-chakra-crown/30 hover:border-divine-gold/40 hover:text-divine-gold/50 transition-all flex flex-col items-center justify-center"
                data-testid="upgrade-module-btn">
                <Crown className="w-6 h-6 mb-1" />
                <span className="font-heading text-sm">Unlock {(user?.module_access || []).includes("relationship") ? "Life Wisdom Coaching" : "Relationship"}</span>
                <span className="text-xs">Upgrade to Bundle</span>
              </button>
            )}
          </div>

          {/* =================== RELATIONSHIP MODULE =================== */}
          {/* =================== RELATIONSHIP MODULE =================== */}
          {activeModule === "relationship" && (isCoachView || ["admin","coach","expert"].includes(user?.role) || (user?.module_access || []).includes("relationship")) && (
            <Suspense fallback={<div className="p-8 text-center text-divine-gold/50 animate-pulse">Loading module...</div>}>
            <RelationshipModule
              token={token} user={user} isCoachView={isCoachView} viewAsUserId={viewAsUserId}
              awareness={awareness} alignment={alignment} awakening={awakening} harmony={harmony}
              progress={progress} overallCompliance={overallCompliance}
              relPipeline={relPipeline} pipelineCompleted={pipelineCompleted} handleTogglePipeline={handleTogglePipeline}
              issueDiagnosis={issueDiagnosis} setIssueDiagnosis={setIssueDiagnosis} relTatkalikDone={relTatkalikDone} relKoshaDone={relKoshaDone}
              relKoshaRetakeKey={relKoshaRetakeKey} handleKoshaStateChange={handleKoshaStateChange}
              lifeDiagnosticKey={lifeDiagnosticKey} handleRelRetakeAll={handleRelRetakeAll}
              regenerating={regenerating} handleRegeneratePipeline={handleRegeneratePipeline}
              weeklySummary={weeklySummary} handleToggleHarmony={handleToggleHarmony}
              handleToggleVerse={handleToggleVerse}
              handleCompletePractice={handleCompletePractice}
            />
            </Suspense>
          )}

          {/* =================== LIFE WISDOM COACHING MODULE =================== */}
          {activeModule === "life" && (isCoachView || ["admin","coach","expert"].includes(user?.role) || (user?.module_access || []).includes("life")) && (
            <Suspense fallback={<div className="p-8 text-center text-divine-gold/50 animate-pulse">Loading module...</div>}>
            <LifeModule
              token={token} user={user} isCoachView={isCoachView} viewAsUserId={viewAsUserId}
              activeModule={activeModule}
              lifeCompliance={lifeCompliance} lifeWeeklySummary={lifeWeeklySummary}
              lifeDiagnosticKey={lifeDiagnosticKey} handleLifeRetakeAll={handleLifeRetakeAll}
              lifeTatkalikDone={lifeTatkalikDone} lifeIssueDiagnosis={lifeIssueDiagnosis}
              setLifeIssueDiagnosis={setLifeIssueDiagnosis}
              lifeKoshaDone={lifeKoshaDone} lifeKoshaRetakeKey={lifeKoshaRetakeKey}
              lifePipeline={lifePipeline} pipelineCompleted={pipelineCompleted}
              handleTogglePipeline={handleTogglePipeline} handleKoshaStateChange={handleKoshaStateChange}
              calendarData={calendarData} calendarMonth={calendarMonth} setCalendarMonth={setCalendarMonth}
              selectedCalendarDate={selectedCalendarDate} setSelectedCalendarDate={setSelectedCalendarDate}
              fetchCalendar={fetchCalendar} streakData={streakData} changeMonth={changeMonth}
              handleToggleHarmony={handleToggleHarmony} practiceAssignments={practiceAssignments}
              handleTogglePracticeCompliance={handleTogglePracticeCompliance}
              awakening={awakening} handleToggleVerse={handleToggleVerse}
              harmony={harmony}
            />
            </Suspense>
          )}
          </>
          )}
        </div>
      </main>
      <OrderReportModal
        open={showOrderReport}
        onClose={(success) => {
          setShowOrderReport(false);
          if (success) {
            // Refresh report data
            const uid = isCoachView ? viewAsUserId : user?.user_id;
            if (uid) apiCall("GET", `/reports-3a/client-reports?user_id=${uid}`, null, token).then(r => { if (r.success) setClientReports(r.data.reports || []); });
          }        }}
        token={token}
        user={user}
        activeModule={activeModule}
      />
      <BookCallModal
        open={showBookCall}
        onClose={() => setShowBookCall(false)}
        token={token}
        user={user}
        activeModule={activeModule}
      />
      <DynamicCtaModal
        cta={selectedDynamicCta}
        open={!!selectedDynamicCta}
        onClose={() => setSelectedDynamicCta(null)}
        token={token}
        user={user}
        activeModule={activeModule}
      />
      <OrderCoachAssessmentModal
        open={showOrderCoachAssessment}
        onClose={(success) => {
          setShowOrderCoachAssessment(false);
          if (success) refreshCoachAssessmentStatus();
        }}
        token={token}
        user={user}
      />
    </div>
  );
};

export default ClientDashboard;
